import { Plus, Minus, HeartHandshake } from "lucide-react";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · Rich diff card for a preferences-update system message in chat.
 * Reads `meta.diff` (built by `customer_preferences._build_diff`) and shows
 * green (+) chips for added tags, red (-) chips for removed tags,
 * plus a compact snapshot of the current preferences.
 *
 * Rendered inline in both `CustomerChat` and `TeamChat` whenever a message
 * carries `sender_role="system"` + `meta.kind="preferences_diff"`.
 */
export function PreferencesDiffCard({ diff, snapshot, testidBase = "pref-diff", label = "Präferenzen aktualisiert" }) {
  if (!diff) return null;
  const rows = [
    { label: "Allergien", diff: diff.allergies || {}, current: snapshot?.allergies || [] },
    { label: "Ernährung", diff: diff.food || {},      current: snapshot?.food || [] },
    { label: "Zuhause",   diff: diff.home || {},      current: snapshot?.home || [] },
  ];
  const anyChange = rows.some((r) => (r.diff.added || []).length + (r.diff.removed || []).length > 0)
    || (diff.notes && diff.notes.changed);
  return (
    <div
      className="border p-3 text-navy-dark"
      style={{
        background: withAlpha(BRAND_COLORS.gold, 0.08),
        borderColor: BRAND_COLORS.gold,
      }}
      data-testid={`${testidBase}-card`}
    >
      <div className="flex items-center gap-2 mb-2">
        <HeartHandshake className="w-4 h-4" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
        <div className="label-caps" style={{ color: BRAND_COLORS.goldDark || "#8A7635" }}>
          {label}
        </div>
      </div>
      {!anyChange ? (
        <div className="text-[12px] text-navy-dark/60 italic" data-testid={`${testidBase}-empty`}>
          Keine strukturellen Änderungen — nur ein erneutes Speichern.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const added = r.diff.added || [];
            const removed = r.diff.removed || [];
            if (added.length === 0 && removed.length === 0) return null;
            return (
              <div key={r.label} data-testid={`${testidBase}-row-${r.label.toLowerCase()}`}>
                <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/55 mb-1">{r.label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {added.map((t, i) => (
                    <span
                      key={`add-${t}-${i}`}
                      className="text-[11.5px] tracking-wide px-2 py-0.5 border inline-flex items-center gap-1"
                      style={{
                        background: withAlpha(STATUS_COLORS.green, 0.12),
                        color: BRAND_COLORS.greenDark || "#1F5233",
                        borderColor: withAlpha(STATUS_COLORS.green, 0.45),
                      }}
                    >
                      <Plus className="w-2.5 h-2.5" strokeWidth={2.5} /> {t}
                    </span>
                  ))}
                  {removed.map((t, i) => (
                    <span
                      key={`rem-${t}-${i}`}
                      className="text-[11.5px] tracking-wide px-2 py-0.5 border inline-flex items-center gap-1 line-through"
                      style={{
                        background: withAlpha(STATUS_COLORS.red, 0.06),
                        color: "#5C1616",
                        borderColor: withAlpha(STATUS_COLORS.red, 0.4),
                      }}
                    >
                      <Minus className="w-2.5 h-2.5" strokeWidth={2.5} /> {t}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
          {diff.notes && diff.notes.changed && (diff.notes.after || diff.notes.before) && (
            <div data-testid={`${testidBase}-row-notes`}>
              <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/55 mb-1">Notiz</div>
              {diff.notes.after && (
                <div className="text-[12px] text-navy-dark italic leading-relaxed">
                  <span style={{ color: BRAND_COLORS.greenDark || "#1F5233" }}>+ </span>
                  {diff.notes.after}
                </div>
              )}
              {diff.notes.before && (
                <div className="text-[11.5px] text-navy-dark/60 italic leading-relaxed line-through">
                  <span style={{ color: "#5C1616" }}>− </span>
                  {diff.notes.before}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
